import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'antd';
import 'react-phone-input-2/lib/style.css';
import { useReturnFormFields } from '../../../../hooks/useReturnFormFields';
import { useRegisterValues } from '../../../../providers/RegisterProvider';
import { Tooltip } from 'components/Shared/Tooltip';
import { Form, FormItem } from 'components/UI/Form';
import { Input } from 'components/UI/Input';
import { useEventbus } from 'hooks/useEventbus';
import * as StyledShared from '../../styles';
import { useAppDispatch } from 'appRedux/hooks';
import { getCreatorsList } from 'appRedux/restApi';
import { AccountFormFields } from 'constants/account/account-form-fields';
import { IS_KIOSK_AM_MODE } from 'constants/app/configs';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import { IFormField } from 'interfaces';

const returnFormFields = (field: IFormField) => {
  switch (field.name) {
    case AccountFormFields.Phone:
      return (
        <FormItem label={field.label} name={field.name} rules={field.rules}>
          <StyledShared.PhoneNumberInput
            containerClass="phone-input-container"
            inputProps={{
              name: 'phone',
              autoFocus: false,
              id: 'phone'
            }}
            country="am"
          />
        </FormItem>
      );
    case AccountFormFields.Password:
    case AccountFormFields.PasswordConfirmation:
      return (
        <FormItem label={field.label} name={field.name} rules={field.rules}>
          <Input type="password" />
        </FormItem>
      );
    case AccountFormFields.TermsAndConditions:
      return (
        <FormItem
          name="terms_and_conditions"
          initialValue={false}
          valuePropName="checked"
          rules={field.rules}
          style={{ marginBottom: 0 }}
        >
          <Checkbox>
            <StyledShared.Label
              dangerouslySetInnerHTML={{ __html: field.label }}
              $required
            />
          </Checkbox>
        </FormItem>
      );
    case AccountFormFields.PromotionalEmails:
      return (
        <FormItem
          name="subscribed_to_news_letter"
          initialValue={false}
          valuePropName="checked"
        >
          <Checkbox>
            <StyledShared.Label
              dangerouslySetInnerHTML={{ __html: field.label }}
            />
          </Checkbox>
        </FormItem>
      );

    default:
      return (
        <FormItem
          label={
            <>
              {field.label}
              {field.tooltip && <Tooltip>{field.tooltip}</Tooltip>}
            </>
          }
          name={field.name}
          rules={field.rules}
          /*
           * Exclusion of UserName is needed as the field already has a pattern
           * which covers the white space scenario. Exclusion of Email is needed
           * as email already handles isWhitespaceForbidden scenario.
           */
          isWhitespaceForbidden={
            field.name !== AccountFormFields.UserName &&
            field.name !== AccountFormFields.Email
          }
        >
          <Input />
        </FormItem>
      );
  }
};

export const RegisterCreatorStepTwo = () => {
  const { form, eventId } = useRegisterValues();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { fireEvent } = useEventbus(
    {
      onEvent: ({ key, id }) => {
        if (id !== eventId) return;
        switch (key) {
          case StepViewerEvents.InitiateNextViewRender:
            form
              .validateFields()
              .then(() => {
                const requestParams = {
                  user_name: form.getFieldValue(AccountFormFields.UserName)
                };
                dispatch(getCreatorsList(requestParams))
                  .then(data => {
                    if (data?.payload?.data?.length > 0) {
                      form.setFields([
                        {
                          name: AccountFormFields.UserName,
                          errors: [t('account.userNameAlreadyTaken')]
                        }
                      ]);
                    } else {
                      fireEvent({
                        key: StepViewerEvents.RenderNextView,
                        id: eventId
                      });
                    }
                  })
                  .catch(() => {});
              })
              .catch(() => {});
            break;
        }
      }
    },
    [form]
  );

  const formFields = useReturnFormFields(form, [
    AccountFormFields.UserName,
    AccountFormFields.Email,
    AccountFormFields.Phone,
    AccountFormFields.Occupation,
    AccountFormFields.Password,
    AccountFormFields.PasswordConfirmation,
    AccountFormFields.TermsAndConditions,
    AccountFormFields.PromotionalEmails
  ]);

  return (
    <StyledShared.Wrapper>
      <Form layout="vertical" form={form} name="registerCreator">
        <input
          className="fake-input"
          id={AccountFormFields.UserName}
          name={AccountFormFields.UserName}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px'
          }}
        />
        <input
          className="fake-input"
          name={AccountFormFields.Password}
          type={AccountFormFields.Password}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: '-9999px'
          }}
        />
        {formFields.map(field => (
          <Fragment key={field.name}>{returnFormFields(field)}</Fragment>
        ))}
        {IS_KIOSK_AM_MODE && (
          <FormItem>
            <StyledShared.AgeLimitMessage>
              {t('account.ageLimitMessage')}
            </StyledShared.AgeLimitMessage>
          </FormItem>
        )}
      </Form>
    </StyledShared.Wrapper>
  );
};
